import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { AppError } from '@st/contract';
import { AuthCard } from '../../components/AuthLayout';
import { HOME_BY_ROLE, useAuth } from '../../auth/AuthProvider';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { Button, Input } from '../../ui';
import { USE_MOCKS } from '../../env';
import { MOCK_PASSWORD } from '../../mocks/data';

const DEMO_ACCOUNTS = [
  { role: 'Klijent', email: 'klijent@soultraining.hr' },
  { role: 'Trener', email: 'trener@soultraining.hr' },
  { role: 'Admin', email: 'admin@soultraining.hr' },
] as const;

/** Vidljivo samo u demo načinu — produkcijski build ovo nikad ne renderira. */
function DemoAccounts({ onPick }: { onPick: (email: string, password: string) => void }) {
  if (!USE_MOCKS) return null;
  return (
    <div className="mt-8 rounded-lg border border-line bg-bg-alt/60 p-4">
      <p className="text-[11px] uppercase tracking-[2px] text-ink-muted">Demo pristup</p>
      <div className="mt-3 flex flex-col gap-2">
        {DEMO_ACCOUNTS.map((a) => (
          <button
            key={a.email}
            type="button"
            onClick={() => onPick(a.email, MOCK_PASSWORD)}
            className="flex items-center justify-between rounded-md px-3 py-2 text-left text-[13px] text-ink transition-colors hover:bg-surface"
          >
            <span className="font-medium">{a.role}</span>
            <span className="text-ink-muted">{a.email}</span>
          </button>
        ))}
      </div>
      <p className="mt-3 text-[12px] text-ink-muted">
        Klikni ulogu pa Prijavi se. Podaci su izmišljeni i ništa se ne sprema.
      </p>
    </div>
  );
}

export function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user = await login(email, password);
      navigate(from ?? HOME_BY_ROLE[user.role], { replace: true });
    } catch (err) {
      // 401 na prijavi znači krive podatke, ne isteklu sesiju.
      setError(
        err instanceof AppError && err.code === 'unauthorized'
          ? t('auth.invalidCredentials')
          : toErrorMessage(t, err).title,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title={t('auth.loginTitle')}
      subtitle={t('auth.loginSubtitle')}
      footer={
        <p className="text-ink-muted">
          {t('auth.noAccount')}{' '}
          <Link to="/registracija" className="text-accent-dark underline">
            {t('auth.register')}
          </Link>
        </p>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          id="login-email"
          type="email"
          label={t('auth.email')}
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          id="login-password"
          type="password"
          label={t('auth.password')}
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p role="alert" className="text-[13px] text-danger">
            {error}
          </p>
        )}
        <Button type="submit" block loading={busy}>
          {t('auth.submitLogin')}
        </Button>
        <Link to="/zaboravljena-lozinka" className="text-center text-[13px] text-ink-muted underline">
          {t('auth.forgot')}
        </Link>
      </form>
      <DemoAccounts
        onPick={(demoEmail, demoPassword) => {
          setEmail(demoEmail);
          setPassword(demoPassword);
          setError(null);
        }}
      />
    </AuthCard>
  );
}
